import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Native install prompt for PWA. Captures `beforeinstallprompt` (Chrome/Edge/Android)
 * and shows a subtle toast-like banner. On iOS Safari we fall back to instructions.
 * Also stores the deferred event on `window.__deferredInstallPrompt` so persistent
 * install buttons elsewhere in the app can reuse it after the banner is dismissed.
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("pwa_installed") === "1") return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) {
      localStorage.setItem("pwa_installed", "1");
      return;
    }
    const dismissed = Number(localStorage.getItem("pwa_dismissed_at") || "0");
    const skipBanner = dismissed && Date.now() - dismissed < 1000 * 60 * 60 * 24 * 7;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;   // share globally with persistent buttons
      window.dispatchEvent(new CustomEvent("pwa-prompt-available"));
      if (!skipBanner) { setDeferred(e); setVisible(true); }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", () => {
      localStorage.setItem("pwa_installed", "1");
      window.__deferredInstallPrompt = null;
      setVisible(false);
    });

    if (skipBanner) return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari fallback: no beforeinstallprompt event, show hint after 8s of use
    const ua = navigator.userAgent;
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
    if (isIos && isSafari) {
      const to = setTimeout(() => { setIosHint(true); setVisible(true); }, 8000);
      return () => { clearTimeout(to); window.removeEventListener("beforeinstallprompt", onBeforeInstall); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  function install() {
    if (!deferred) return;
    deferred.prompt();
    deferred.userChoice.then(() => {
      setDeferred(null);
      setVisible(false);
    });
  }
  function dismiss() {
    localStorage.setItem("pwa_dismissed_at", String(Date.now()));
    setVisible(false);
  }

  if (!visible) return null;
  return (
    <div data-testid="install-prompt"
      className="fixed z-50 bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm
                 bg-[#1C1917] text-white rounded-2xl shadow-2xl p-5 flex items-start gap-3 fade-in-up">
      <div className="w-10 h-10 rounded-xl bg-[#D4A373] flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-stone-900" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading text-lg font-light leading-tight">{t("install.title")}</p>
        {iosHint ? (
          <p className="text-xs text-stone-400 mt-1 leading-snug">{t("install.iosHint")}</p>
        ) : (
          <p className="text-xs text-stone-400 mt-1 leading-snug">{t("install.hint")}</p>
        )}
        {!iosHint && (
          <button onClick={install} data-testid="install-btn"
            className="mt-3 px-4 py-1.5 rounded-full bg-[#D4A373] text-stone-900 text-xs uppercase tracking-widest hover:bg-white transition-colors">
            {t("install.cta")}
          </button>
        )}
      </div>
      <button onClick={dismiss} data-testid="install-dismiss" className="text-stone-500 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
